import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ServiceOrder {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  service_id: string | null;
  service_name: string;
  project_description: string | null;
  budget_range: string | null;
  timeline: string | null;
  brief_answers: Record<string, any>;
  status: string;
  created_at: string;
  updated_at: string;
}

export const useServiceOrders = () => {
  return useQuery({
    queryKey: ["service-orders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_orders")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ServiceOrder[];
    },
  });
};

export const useCreateServiceOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderData: Omit<ServiceOrder, "id" | "created_at" | "updated_at" | "status">) => {
      const { data, error } = await supabase
        .from("service_orders")
        .insert([orderData])
        .select()
        .single();

      if (error) throw error;
      return data as ServiceOrder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-orders"] });
      toast.success("Հայտը հաջողությամբ ուղարկվեց");
    },
    onError: (error: any) => {
      console.error("Error creating service order:", error);
      
      // Provide specific error messages based on error type
      if (error?.message?.includes("permission denied")) {
        toast.error("Դուք չունեք հայտ ուղարկելու իրավունք");
      } else if (error?.message?.includes("violates")) {
        toast.error("Խնդրում ենք ստուգել լրացված տվյալները");
      } else {
        toast.error("Սխալ՝ հայտը ուղարկելիս");
      }
    },
  });
};

export const useUpdateServiceOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ServiceOrder> & { id: string }) => {
      const { data, error } = await supabase
        .from("service_orders")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as ServiceOrder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-orders"] });
      toast.success("Հայտը թարմացվեց");
    },
    onError: (error) => {
      console.error("Error updating service order:", error);
      toast.error("Սխալ՝ հայտը թարմացնելիս");
    },
  });
};
